import React, { useState, useEffect } from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import { ScreenType } from "../../../../types/subscription/subscriptionScreen/subscriptionScreen.type";
import {
  useGetOrganisationsQuery,
  useGetProfileQuery,
} from "../../../../services";
import { useAppSelector } from "../../../../redux/hooks";
import { RootState } from "../../../../redux/store";
import {
  useAddScreensMutation,
  useGetPlansQuery,
} from "../../../../services/subscription";

interface AddScreenModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddScreenModal: React.FC<AddScreenModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { theme } = useTheme();
  const [premiumCount, setPremiumCount] = useState(0);
  const [normalCount, setNormalCount] = useState(0);

  // get auth/admin first (used by queries)
  const { admin, isLoggedIn } = useAppSelector(
    (state: RootState) => state.admin
  );

  const { data: profileResp } = useGetProfileQuery(undefined, {
    skip: !isLoggedIn,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { data: organisationsResp, isLoading: orgsLoading } =
    useGetOrganisationsQuery(undefined, {
      skip: !isLoggedIn,
    });

  // Fetch plans
  const { data: plansResp, isLoading: plansLoading } = useGetPlansQuery(
    undefined,
    {
      skip: !isLoggedIn,
    }
  );
  const [addScreens, { isLoading: adding }] = useAddScreensMutation();

  const assignedOrgsRaw = (profileResp as any)?.result?.assignedOrganisations;
  const assignedOrgIds: string[] = Array.isArray(assignedOrgsRaw)
    ? assignedOrgsRaw
        .map((o: any) => (typeof o === "string" ? o : o?._id))
        .filter(Boolean)
    : [];
  const assignedOrgsObjs: any[] = Array.isArray(assignedOrgsRaw)
    ? assignedOrgsRaw.filter((o: any) => typeof o === "object" && o)
    : [];

  const allOrgs: any[] = Array.isArray((organisationsResp as any)?.data)
    ? (organisationsResp as any).data
    : Array.isArray(organisationsResp)
    ? (organisationsResp as any[])
    : [];
  const filteredOrgs = allOrgs.filter((org) =>
    assignedOrgIds.includes(org?._id)
  );
  const options =
    filteredOrgs.length > 0
      ? filteredOrgs.map((org) => ({
          value: String(org?._id),
          label: String(org?.name ?? org?._id),
          currency: org?.currency ?? "",
        }))
      : assignedOrgsObjs.length > 0
      ? assignedOrgsObjs.map((org) => ({
          value: String(org?._id),
          label: String(org?.name ?? org?._id),
        }))
      : assignedOrgIds.map((id) => ({ value: String(id), label: String(id) }));

  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  useEffect(() => {
    if (!selectedOrgId && assignedOrgIds.length > 0) {
      setSelectedOrgId(assignedOrgIds[0]);
    }
  }, [assignedOrgIds, selectedOrgId]);

  const incrementPremium = () => setPremiumCount((prev) => prev + 1);
  const decrementPremium = () =>
    setPremiumCount((prev) => (prev > 0 ? prev - 1 : 0));
  const incrementNormal = () => setNormalCount((prev) => prev + 1);
  const decrementNormal = () =>
    setNormalCount((prev) => (prev > 0 ? prev - 1 : 0));

  const plans: any[] = Array.isArray((plansResp as any)?.results)
    ? (plansResp as any).results
    : Array.isArray((plansResp as any)?.data?.results)
    ? (plansResp as any).data.results
    : Array.isArray(plansResp)
    ? (plansResp as any[])
    : [];

  const premiumPlan = plans.find((p) => p?.type === ScreenType.PREMIUM);
  const basicPlan = plans.find((p) => p?.type === ScreenType.BASIC);

  // Find selected org object to get currency
  const selectedOrgObj =
    filteredOrgs.find((org) => String(org?._id) === selectedOrgId) ||
    assignedOrgsObjs.find((org) => String(org?._id) === selectedOrgId);

  // Always use org currency for price/tax lookup
  const selectedCurrency = selectedOrgObj?.currency?.toUpperCase?.() || "INR";

  // Currency symbol map
  const currencySymbols: Record<string, string> = {
    INR: "₹",
    AED: "د.إ",
    USD: "$",
  };
  const currencySymbol = currencySymbols[selectedCurrency] || selectedCurrency;

  // Always pick price/tax by selected currency
  const pickPrice = (plan: any, currency = "INR") => {
    const prices = Array.isArray(plan?.prices) ? plan.prices : [];
    return (
      prices.find((x: any) => x?.currency?.toUpperCase?.() === currency) ||
      prices[0]
    );
  };

  // Breakdown always uses selected currency
  const breakdown = (count: number, plan: any) => {
    if (!plan || !count) return { subtotal: 0, tax: 0, total: 0, unitPrice: 0 };
    const priceObj = pickPrice(plan, selectedCurrency);
    const unitPrice = Number(priceObj?.price ?? 0);
    const subtotal = count * unitPrice;

    const taxType = priceObj?.tax?.type as
      | "INCLUSIVE"
      | "EXCLUSIVE"
      | undefined;
    const taxPercent = Number(priceObj?.tax?.percent ?? 0);
    let tax = 0;
    let total = subtotal;

    if (taxPercent > 0) {
      if (taxType === "EXCLUSIVE") {
        tax = subtotal * (taxPercent / 100);
        total = subtotal + tax;
      } else {
        // INCLUSIVE: unit price already includes tax
        tax = subtotal - subtotal / (1 + taxPercent / 100);
        total = subtotal;
      }
    }

    return { subtotal, tax, total, unitPrice, taxPercent };
  };

  // Use selected currency for both Premium and Normal
  const premiumBD = breakdown(premiumCount, premiumPlan);
  const basicBD = breakdown(normalCount, basicPlan);

  const premiumTotal = premiumBD.subtotal;
  const basicTotal = basicBD.subtotal;

  const subTotal = premiumBD.subtotal + basicBD.subtotal;
  const taxes = premiumBD.tax + basicBD.tax;
  const grandTotal = premiumBD.total + basicBD.total;

  const handleSubscribe = async () => {
    const products: { plan: string; quantity: number }[] = [];
    if (premiumCount > 0 && premiumPlan?._id)
      products.push({ plan: premiumPlan._id, quantity: premiumCount });
    if (normalCount > 0 && basicPlan?._id)
      products.push({ plan: basicPlan._id, quantity: normalCount });

    if (!selectedOrgId || products.length === 0) return;

    try {
      const resp = await addScreens({
        organisation: selectedOrgId,
        products,
        currentBilledAmount: Number(grandTotal.toFixed(2)),
        currentBilledTaxAmount: Number(taxes.toFixed(2)),
      }).unwrap();

      // Call onSuccess callback
      onSuccess?.();

      if (resp?.result?.invoice?.invoiceUrl) {
        window.open(resp.result.invoice.invoiceUrl, "_blank");
        onClose();
      } else {
        onClose();
      }
    } catch {
      // optionally show a toast/error UI
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1" onClick={onClose} />
      <div
        className={`w-80 h-full ${
          theme === "dark" ? "bg-[#1F1F1F]" : "bg-white"
        } shadow-xl transform transition-transform duration-300 ease-in-out`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`p-4 border-b ${
            theme === "dark" ? "border-[#2A2A2A]" : "border-gray-200"
          }`}
        >
          <div className="flex items-center justify-between">
            <h3
              className={`text-lg font-semibold ${
                theme === "dark" ? "text-white" : "text-gray-900"
              }`}
            >
              Add New Screens
            </h3>
            <button
              onClick={onClose}
              className={`p-2 rounded-full transition-colors ${
                theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5"
              }`}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6">
          {/* Choose Organization */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                theme === "dark" ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Choose Organization
            </label>
            <select
              className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#B6CB24] ${
                theme === "dark"
                  ? "bg-[#2A2A2A] border-gray-600 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              }`}
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              disabled={options.length === 0}
            >
              {options.length === 0 && (
                <option>No organizations available</option>
              )}
              {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Premium Screen */}
          {/* Optionally show loading indicator for plans */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                theme === "dark" ? "text-gray-300" : "text-gray-700"
              }`}
            >
              {ScreenType.PREMIUM} Screen
            </label>
            <div
              className={`flex items-center justify-between border rounded-md ${
                theme === "dark"
                  ? "bg-[#2A2A2A] border-gray-600 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              }`}
            >
              <button
                onClick={decrementPremium}
                className={`w-8 h-8 rounded-full border flex items-center justify-center ${
                  theme === "dark"
                    ? "border-gray-600 text-gray-400 hover:bg-white/10"
                    : "border-gray-300 text-gray-600 hover:bg-black/5"
                }`}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M19 13H5v-2h14v2z" />
                </svg>
              </button>
              <span
                className={`text-lg font-medium ${
                  theme === "dark" ? "text-white" : "text-gray-900"
                }`}
              >
                {premiumCount.toString().padStart(2, "0")}
              </span>
              <button
                onClick={incrementPremium}
                className={`w-8 h-8 rounded-full border flex items-center justify-center ${
                  theme === "dark"
                    ? "border-gray-600 text-gray-400 hover:bg-white/10"
                    : "border-gray-300 text-gray-600 hover:bg-black/5"
                }`}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Basic Screen */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                theme === "dark" ? "text-gray-300" : "text-gray-700"
              }`}
            >
              {ScreenType.BASIC} screen
            </label>
            <div
              className={`flex items-center justify-between border rounded-md ${
                theme === "dark"
                  ? "bg-[#2A2A2A] border-gray-600 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              }`}
            >
              <button
                onClick={decrementNormal}
                className={`w-8 h-8 rounded-full border flex items-center justify-center ${
                  theme === "dark"
                    ? "border-gray-600 text-gray-400 hover:bg-white/10"
                    : "border-gray-300 text-gray-600 hover:bg-black/5"
                }`}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M19 13H5v-2h14v2z" />
                </svg>
              </button>
              <span
                className={`text-lg font-medium ${
                  theme === "dark" ? "text-white" : "text-gray-900"
                }`}
              >
                {normalCount.toString().padStart(2, "0")}
              </span>
              <button
                onClick={incrementNormal}
                className={`w-8 h-8 rounded-full border flex items-center justify-center ${
                  theme === "dark"
                    ? "border-gray-600 text-gray-400 hover:bg-white/10"
                    : "border-gray-300 text-gray-600 hover:bg-black/5"
                }`}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Cost Details */}
          <div>
            <h4
              className={`text-sm font-medium mb-3 ${
                theme === "dark" ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Cost Details
            </h4>
            <div className="space-y-2 min-h-[180px]">
              <div className="flex justify-between items-center text-sm">
                <span
                  className={
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }
                  style={{ width: 80 }}
                >
                  Type
                </span>
                <span
                  className={
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }
                  style={{ width: 60, textAlign: "center" }}
                >
                  Count
                </span>
                <span
                  className={
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }
                  style={{ width: 80, textAlign: "right" }}
                >
                  Cost {currencySymbol}
                </span>
              </div>
              {/* Always show Premium and Basic rows, even if count is 0 */}
              <div className="flex justify-between items-center text-sm">
                <span
                  style={{ width: 80 }}
                  className={theme === "dark" ? "text-white" : "text-gray-900"}
                >
                  {ScreenType.PREMIUM}
                </span>
                <span
                  style={{ width: 60, textAlign: "center" }}
                  className={theme === "dark" ? "text-white" : "text-gray-900"}
                >
                  {premiumCount}
                </span>
                <span
                  style={{ width: 80, textAlign: "right" }}
                  className={theme === "dark" ? "text-white" : "text-gray-900"}
                >
                  {premiumTotal.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span
                  style={{ width: 80 }}
                  className={theme === "dark" ? "text-white" : "text-gray-900"}
                >
                  {ScreenType.BASIC}
                </span>
                <span
                  style={{ width: 60, textAlign: "center" }}
                  className={theme === "dark" ? "text-white" : "text-gray-900"}
                >
                  {normalCount}
                </span>
                <span
                  style={{ width: 80, textAlign: "right" }}
                  className={theme === "dark" ? "text-white" : "text-gray-900"}
                >
                  {basicTotal.toFixed(2)}
                </span>
              </div>
              {/* Always show subtotal/tax/total, even if both counts are 0 */}
              <hr
                className={`my-2 ${
                  theme === "dark" ? "border-gray-600" : "border-gray-300"
                }`}
              />
              <div className="flex justify-between items-center text-sm">
                <span
                  style={{ width: 80 }}
                  className={theme === "dark" ? "text-white" : "text-gray-900"}
                >
                  Sub Total
                </span>
                <span style={{ width: 60 }}></span>
                <span
                  style={{ width: 80, textAlign: "right" }}
                  className={theme === "dark" ? "text-white" : "text-gray-900"}
                >
                  {subTotal.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span
                  className={theme === "dark" ? "text-white" : "text-gray-900"}
                >
                  Taxes ({currencySymbol}
                  {premiumCount > 0
                    ? premiumBD.taxPercent
                    : normalCount > 0
                    ? basicBD.taxPercent
                    : 0}
                  %)
                </span>
                <span style={{ width: 60 }}></span>
                <span
                  style={{ width: 80, textAlign: "right" }}
                  className={theme === "dark" ? "text-white" : "text-gray-900"}
                >
                  {taxes.toFixed(2)}
                </span>
              </div>
              <hr
                className={`my-2 ${
                  theme === "dark" ? "border-gray-600" : "border-gray-300"
                }`}
              />
              <div className="flex justify-between items-center font-semibold">
                <span
                  style={{ width: 80 }}
                  className={theme === "dark" ? "text-white" : "text-gray-900"}
                >
                  Total
                </span>
                <span style={{ width: 60 }}></span>
                <span
                  style={{ width: 80, textAlign: "right" }}
                  className={theme === "dark" ? "text-white" : "text-gray-900"}
                >
                  {grandTotal.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div
          className={`absolute bottom-0 left-0 right-0 p-6 border-t space-y-3 ${
            theme === "dark" ? "border-[#2A2A2A]" : "border-gray-200"
          }`}
        >
          <button
            className="w-full px-4 py-2 font-medium text-black rounded-[10px] border"
            style={{
              border: "1px solid var(--menu-active-border, #C6D92C)",
              background:
                "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
            }}
            onClick={handleSubscribe}
            disabled={
              adding || !selectedOrgId || premiumCount + normalCount === 0
            }
          >
            {adding ? "Subscribing..." : "Subscribe"}
          </button>
          <button
            onClick={onClose}
            className={`w-full px-4 py-2 font-medium rounded-[10px] border ${
              theme === "dark"
                ? "border-gray-600 text-gray-300 hover:bg-white/10"
                : "border-gray-300 text-gray-700 hover:bg-black/5"
            }`}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddScreenModal;
